import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../helpers/permissionModule";
import { RootState, useAppDispatch, useAppSelector } from "../../../store";
import FullScreenLoader from "../../Components/FullScreenLoader";
import NoPermissionPage from "../../Components/NoPermissionPage";
import { svg } from "../../../Assets/svg";
import {
  formatGBP,
  formatCurrency,
  getAntdSortOrder,
  useDebounce,
} from "../../../helpers/utils";
import NotificationButton from "../../Components/NotificationButton";
import CreateDeliveryLocationModal from "./Components/Modals/CreateDeliveryLocationModal";
import {
  deleteDeliveryLocationAPI,
  getDeliveryLocationListAPI,
} from "./deliveryLocationApis";
import NoDataMessage from "../../Components/NoData";
import InfiniteScroll from "react-infinite-scroll-component";
import { Dropdown, Menu, Spin, Table, TableColumnsType, Tooltip } from "antd";
import { CommonConfirmationModal } from "../../../Components/CommonConfirmationModal";

export interface DeliveryLocation {
  _id: string;
  name: string;
  type: DeliveryLocationType;
  qty_type: DeliveryLocationQtyType;
  qty_inc: number;
  price: number;
  half_trailer_price?: number;
  boxes?: any[];
  parcel_boxes?: any[];
  box_options?: any[];
  method?: string;
}

export enum DeliveryLocationType {
  FREE = "free",
  PAID = "paid",
}
export enum DeliveryLocationQtyType {
  TRAILERS = "trailers",
  PALLETS = "pallets",
  FULL_TRAILERS = "full_trailers",
  HALF_TRAILERS = "half_trailers",
  PARCEL_BOX = "parcel_box",
}

export const DeliveryLocationTypeColors: Record<DeliveryLocationType, string> =
  {
    [DeliveryLocationType.FREE]: "#419F20", // Green
    [DeliveryLocationType.PAID]: "#E5002B", // Red
  };

export const DeliveryLocationQtyTypeColors: Record<
  DeliveryLocationQtyType,
  string
> = {
  [DeliveryLocationQtyType.TRAILERS]: "#FF8000", // Orange
  [DeliveryLocationQtyType.PALLETS]: "#167FFF", // Blue
  [DeliveryLocationQtyType.FULL_TRAILERS]: "#FF8000", // Purple
  [DeliveryLocationQtyType.HALF_TRAILERS]: "#FF8000", // Coral
  [DeliveryLocationQtyType.PARCEL_BOX]: "#000", // Purple
};

/**
 * Reusable helper functions for Delivery Location Labels
 */
export const getDeliveryQtyTypeLabel = (qtyType: string) => {
  switch (qtyType) {
    case DeliveryLocationQtyType.TRAILERS:
      return "Trailer";
    case DeliveryLocationQtyType.PALLETS:
      return "Pallet";
    case DeliveryLocationQtyType.FULL_TRAILERS:
      return "Full Trailer";
    case DeliveryLocationQtyType.HALF_TRAILERS:
      return "Half Trailer";
    case DeliveryLocationQtyType.PARCEL_BOX:
    case "parcel_box":
      return "Parcel Box";
    default:
      return qtyType || "-";
  }
};

export const getDeliveryPriceLabel = (price: number, currency: string = "GBP", conversion_rate: number = 1) => {
  return price ? formatCurrency(price, currency, conversion_rate) : "-";
};

export const ColoredTypeTag = ({
  value,
  type,
}: {
  value: string;
  type: "type" | "qty_type";
}) => {
  if (!value) return <span>-</span>;
  const color =
    type === "type"
      ? DeliveryLocationTypeColors[value as DeliveryLocationType]
      : DeliveryLocationQtyTypeColors[value as DeliveryLocationQtyType];

  return (
    <span
      style={{
        color: color || "inherit",
        textTransform: "capitalize",
        fontWeight: 500,
      }}
    >
      {value}
    </span>
  );
};

const DeliveryLocations = () => {
  const dispatch = useAppDispatch();
  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData?.MASTER_DATA,
  );
  const { authLoading } = useAppSelector((state: RootState) => state?.loading);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);

  const [deliveryLocations, setDeliveryLocations] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit] = useState<number>(30);
  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, 300);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState(0);
  // const [rerendorHiredCompany, setRerendorHiredCompany] = useState(false);
  const [sorterState, setSorterState] = useState<{
    field: string | null;
    order: "asc" | "desc" | null;
  }>({ field: null, order: null });

  const abortControllerRef = useRef<AbortController | null>(null);

  const [selectedDeliveryLocation, setSelectedDeliveryLocation] =
    useState<any>(null);

  const [idToDelete, setIdToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchDeliveryLocations = (page: number, isReset: boolean) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (isReset) {
      setLoading(true);
    }

    const filters = {
      page: page,
      limit: limit,
      search: debouncedSearch,
      sortBy: sorterState.field,
      sortOrder: sorterState.order,
    };

    dispatch(
      getDeliveryLocationListAPI({
        filters,
        signal: controller.signal,
        onSuccess: (data: any) => {
          const newData = data?.delivery_locations || [];
          const total = data?.total || 0;
          setTotalCount(total);

          if (isReset) {
            setDeliveryLocations(newData);
            window.scrollTo(0, 0);
          } else {
            setDeliveryLocations((prev) => [...prev, ...newData]);
          }

          const currentCount = isReset
            ? newData.length
            : deliveryLocations.length + newData.length;
          setHasMore(currentCount < total);
          setLoading(false);
        },
        onError: () => {
          setLoading(false);
        },
      }),
    );
  };

  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    fetchDeliveryLocations(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, sorterState]);

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchDeliveryLocations(nextPage, false);
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    const order =
      sorter.order === "ascend"
        ? "asc"
        : sorter.order === "descend"
        ? "desc"
        : null;
    setSorterState({ field: sorter.field, order: order as any });
  };

  const handleEditClick = (record: any) => {
    setSelectedDeliveryLocation(record);
    setIsCreateModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!idToDelete) return;

    setDeleting(true);

    dispatch(
      deleteDeliveryLocationAPI({
        id: idToDelete,
        onSuccess: () => {
          setDeleting(false);
          setIdToDelete(null);
          fetchDeliveryLocations(1, true);
        },
        onError: () => {
          setDeleting(false);
        },
      }),
    );
  };

  const handleCloseDeleteModal = () => {
    setIdToDelete(null);
  };

  const MoreOptionsMenu = (data: any) => {
    return (
      <Menu>
        {USER_PERMISSIONS.MODIFY && (
          <Menu.Item key="1" onClick={() => handleEditClick(data)}>
            <div className="text-black d-flex align-items-center gap-2 svg-20">
              Modify
            </div>
          </Menu.Item>
        )}

        {/* ✅ Remove Guard */}
        {USER_PERMISSIONS.REMOVE && (
          <Menu.Item key="2" onClick={() => setIdToDelete(data._id)}>
            <div className="text-danger d-flex align-items-center gap-2 svg-20">
              Remove
            </div>
          </Menu.Item>
        )}
      </Menu>
    );
  };

  const columns: TableColumnsType<any> = [
    {
      title: "",
      dataIndex: "is_sage_synced",
      key: "is_sage_synced",
      align: "center" as const,
      render: (is_sage_synced: any, record: any) => {
        return (
          <Tooltip title={is_sage_synced ? "Data Synced" : "Data Not Synced"}>
            <span className="d-flex align-items-center justify-content-center svg-20 cursor-pointer">
              {is_sage_synced ? svg?.sageIconSync : svg?.sageNonSyncIcon}
            </span>
          </Tooltip>
        );
      },
      width: 50,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      sortOrder: getAntdSortOrder("name", sorterState),
      render: (name: string, record: any) => (
        <div className="d-flex align-items-center">{name}</div>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      sorter: true,
      sortOrder: getAntdSortOrder("type", sorterState),
      render: (type: string, record: any) => (
        <ColoredTypeTag type="type" value={type} />
      ),
    },
    {
      title: "Qty Type",
      dataIndex: "qty_type",
      key: "qty_type",
      sorter: true,
      sortOrder: getAntdSortOrder("qty_type", sorterState),
      render: (qtyType: string, record: any) => (
        <ColoredTypeTag type="qty_type" value={qtyType} />
      ),
    },
    {
      title: "Qty Increment",
      dataIndex: "qty_inc",
      key: "qty_inc",
      sorter: true,
      sortOrder: getAntdSortOrder("qty_inc", sorterState),
      render: (qty: string, record: any) => (
        <div className="d-flex align-items-center">{qty ?? "-"}</div>
      ),
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      sorter: true,
      sortOrder: getAntdSortOrder("price", sorterState),
      render: (price: number, record: any) => (
        <div className="d-flex align-items-center">
          {price ? formatGBP(price) : "-"}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div
          className="d-flex justify-content-center"
          onClick={(e) => e.stopPropagation()}
        >
          <Dropdown
            overlay={() => MoreOptionsMenu(record)}
            trigger={["click"]}
            placement="bottomRight"
            className="cursor-pointer"
            overlayClassName="moreOptionDropdown"
          >
            <span className="svg-32 svg-outline-primary" role="button">
              {svg?.tableMoreOption}
            </span>
          </Dropdown>
        </div>
      ),
      width: 10,
    },
  ];

  return USER_PERMISSIONS?.READ ? (
    <>
      {" "}
      <div className="page-content h-100 px-40 py-40">
        <div className="mb-md-3 mb-2 d-flex flex-column gap-md-2 gap-0">
          <div className="d-flex flex-sm-nowrap flex-wrap gap-sm-3 gap-2 justify-content-between align-items-center">
            <h2 className="text-black fw-bolder page-title mb-0 order-sm-1 order-1 w-mob-60">
              <span>Delivery Locations</span>{" "}
              {totalCount > 0 && `(${totalCount})`}
            </h2>
            <div
              className={`pageSearch mw-100 position-relative d-inline-block order-sm-2 order-3 ms-auto`}
            >
              <input
                type="text"
                value={search}
                className="form-control form-control-lg"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value?.trim();
                  setSearch(value === "" ? "" : e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search"
              />
              {search?.length ? (
                <span
                  className="cursor-pointer"
                  onClick={() => {
                    setSearch("");
                    setCurrentPage(1);
                  }}
                >
                  {svg?.searchClear}
                </span>
              ) : (
                <span>{svg?.search}</span>
              )}
            </div>
            <div className="d-flex gap-sm-3 gap-2 order-sm-3 order-2">
              {USER_PERMISSIONS?.ADD ? (
                <button
                  className="btn btn-lg btn-primary mob-btn px-md-4 px-3"
                  onClick={() => {
                    setSelectedDeliveryLocation(null);
                    setIsCreateModalOpen(true);
                  }}
                >
                  + CREATE
                </button>
              ) : (
                ""
              )}
              <NotificationButton />
            </div>
          </div>
        </div>

        <div className="card page-table-card border-0 shadow rounded-4 flex-fill overflow-y-auto">
          <div className="card-body overflow-y-auto" id="scrollableDiv">
            {loading && currentPage === 1 ? (
              <FullScreenLoader />
            ) : deliveryLocations.length === 0 ? (
              <NoDataMessage
                icon={svg?.noDeliveryLocations}
                text="You do not have any delivery locations yet."
              />
            ) : (
              <InfiniteScroll
                dataLength={deliveryLocations.length}
                next={handleLoadMore}
                hasMore={hasMore}
                loader={
                  <div className="text-center p-2">
                    <Spin />
                  </div>
                }
                scrollableTarget="scrollableDiv"
                height={"100%"}
                style={{ overflow: "auto" }}
              >
                <Table
                  dataSource={deliveryLocations}
                  scroll={{ x: "max-content" }}
                  pagination={false}
                  columns={columns}
                  onChange={handleTableChange}
                  rowKey={(record) => record._id}
                  className="custom-table"
                  bordered
                />
              </InfiniteScroll>
            )}
          </div>
        </div>
      </div>
      {isCreateModalOpen && (
        <CreateDeliveryLocationModal
          open={isCreateModalOpen}
          onClose={() => {
            setSelectedDeliveryLocation(null);
            setIsCreateModalOpen(false);
          }}
          refreshData={() => fetchDeliveryLocations(1, true)}
          editData={selectedDeliveryLocation}
        />
      )}
      <CommonConfirmationModal
        open={idToDelete != null}
        title="Remove Delivery Location, Are You Sure?"
        subTitle="Removing this Delivery Location will delete it from the list, and it will no longer be available for selection. Please confirm before proceeding."
        loading={deleting}
        onSuccess={handleDeleteConfirm}
        onClose={handleCloseDeleteModal}
      />
    </>
  ) : authLoading ? (
    <FullScreenLoader />
  ) : (
    <NoPermissionPage />
  );
};

export default DeliveryLocations;
